import { computed, inject, Injectable, signal } from '@angular/core';

import { Region } from '../../config/app-config';
import { EnvironmentService } from '../../services/environment.service';

@Injectable({ providedIn: 'root' })
export class GlobalsService {
  private readonly env = inject(EnvironmentService);
  // `null` is impossible in practice: dev pins to 'EU' at startup and
  // regionSelectedGuard blocks the app shell until staging/prod has a region.
  readonly server: Region = this.env.getRegion() ?? 'EU';
  readonly otherRegion: Region = this.server === 'EU' ? 'CN' : 'EU';

  /**
   * Refcount of in-flight requests against managed backends (supergodUrl,
   * supergodOtherServerUrl, bhiveUrl). When > 0 the app shows the
   * full-screen blocking overlay.
   */
  private readonly managedCount = signal(0);

  /**
   * Refcount of in-flight requests to any other host. Drives the slim topbar
   * progress bar — non-blocking.
   */
  private readonly otherCount = signal(0);

  readonly isManagedLoading = computed(() => this.managedCount() > 0);
  readonly isOtherLoading = computed(() => this.otherCount() > 0);
  readonly isLoading = computed(() => this.isManagedLoading() || this.isOtherLoading());

  readonly serverLocation = computed(() => (this.server === 'EU' ? 'Europe' : 'China'));
  readonly otherServer = computed(() =>
    this.serverLocation() === 'Europe' ? 'China' : 'Europe'
  );

  beginManagedCall(): void {
    this.managedCount.update((n) => n + 1);
  }

  endManagedCall(): void {
    this.managedCount.update((n) => Math.max(0, n - 1));
  }

  beginOtherCall(): void {
    this.otherCount.update((n) => n + 1);
  }

  endOtherCall(): void {
    this.otherCount.update((n) => Math.max(0, n - 1));
  }

  /**
   * Manual overlay control kept for legacy callers (showSpinner/hideSpinner).
   * Bumps the managed counter so component-driven loads stack cleanly with
   * the HTTP interceptor's auto-tracking.
   */
  showSpinner(): void {
    this.beginManagedCall();
  }

  hideSpinner(): void {
    this.endManagedCall();
  }
}
